<template>
  <div class="contentInfo">
    <div
      class="row gutters-sm"
    >
      <div class="col-auto">
        <h3>
          Enter your URLs
        </h3>
        <p>
          These URLs will be used to add content to your creatives and to match your adverts
          to contextually relevant content on Smartology's network of premium publishers.
          More content generally leads to greater engagement.
        </p>
      </div>
    </div>
    <b-form
      ref="contentForm"
      @submit="onSubmit"
    >
      <div class="form-group content-wrapper">
        <b-row
          v-for="(contentItem, index) in urlsListWithBlank"
          :key="index"
        >
          <b-col
            lg="12"
            class="mb-2"
          >
            <div
              class="row gutters-xs"
            >
              <div class="col">
                <div class="input">
                  <span class="input-icon-addon">
                    {{ index + 1 }}
                  </span>

                  <b-form-input
                    :value="contentItem.url"
                    type="url"
                    class="form-control"
                    placeholder="Enter a URL"
                    pattern="https?://.+"
                    :data-index="index"
                    :state="validURL(urlsListWithBlank[index].url)"
                    :disabled="inputDisabled(contentItem.url, index)"
                    @input="updateInput($event, index)"
                    @paste.prevent="onPaste($event, index)"
                    data-test="content-scrape-item"
                    :ref="`input`"
                  />
                  <b-form-invalid-feedback
                    id="input-live-feedback"
                    data-test="content-scrape-item-invalid-feedback"
                  >
                    Please enter a valid Url
                  </b-form-invalid-feedback>
                </div>
              </div>
              <span
                v-if="canDeleteContentItem(contentItem.url, index)"
                class="col-auto"
              >
                <button
                  class="btn btn-danger"
                  type="button"
                  @click="deleteUrl(index)"
                  :disabled="GET_IS_CAMPAIGN_READONLY || isContentScrapesInProgress"
                >
                  <i class="fa-light fa-trash-can" />
                </button>
              </span>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-form>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as contentTypes from '@/modules/content/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import helper from '@/core/helper';

export default {
  name: 'ContentInfo',
  data() {
    return {
      urlsList: [],
    };
  },
  computed: {
    ...mapState('content', {
      scrapedContent: 'newScrapedContent',
    }),
    ...mapState('content', [
      'inProgressContentScrapes',
      'scrapingBatchId',
      'feedId',
    ]),
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
      campaignTypes.GET_IS_VIEW_TYPE,
    ]),
    urlsListWithBlank() {
      return this.urlsList.concat({
        url: '',
      });
    },
    isContentScrapesInProgress() {
      return this.inProgressContentScrapes.totalStartLength > 0;
    },
    disableDelete() {
      return !this.GET_IS_VIEW_TYPE('Setup');
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.SCRAPE_CONTENT,
      contentTypes.RESCRAPE_CONTENT,
      contentTypes.FETCH_ARTICLES,
      contentTypes.NEW_FETCH_CONTENT_SCRAPES,
      contentTypes.SET_CONTENT_ITEMS_URLS_CHANGED,
    ]),
    canDeleteContentItem(url, index) {
      if (!this.disableDelete || url === '') return true;
      if (index < this.scrapedContent.length && this.scrapedContent[index].url === url) return false;
      return true;
    },
    updateInput(value, index) {
      if (index === this.urlsListWithBlank.length - 1) {
        this.urlsList.push({
          url: '',
        });
      }
      this.urlsList.splice(index, 1, {
        url: value.trim(),
      });
      if (this.validURL(value.trim()) || value === '') this.emitNavigationValidity(true);
      else this.emitNavigationValidity(false);
    },
    onPaste(event, index) {
      const clipboardData = event.clipboardData.getData('text')
        .split(/\r?\n/)
        .filter((el) => el !== '');
      if (clipboardData.length === 0) return;
      const pasteList = [];
      for (let i = 0; i < clipboardData.length; i++) {
        pasteList.push({
          url: clipboardData[i],
        });
      }
      this.urlsList.splice(index, pasteList.length, ...pasteList);
      this.checkAllValidUrls();
    },
    deleteUrl(index) {
      this.urlsList.splice(index, 1);
      this.checkAllValidUrls();
    },
    validURL(str) {
      if (str === '') return;
      return helper.validURL(str);
    },
    inputDisabled(url, index) {
      return (this.feedId === 0
        || this.GET_IS_CAMPAIGN_READONLY
        || !this.canDeleteContentItem(url, index))
        || this.isContentScrapesInProgress;
    },
    hasChanged() {
      if (this.scrapedContent.length !== this.urlsList.length) return true;
      return JSON.stringify(this.scrapedContent) !== JSON.stringify(this.urlsList);
    },
    onSubmit() {
      const urls = [];
      for (let i = 0; i < this.urlsList.length; i++) {
        if (this.urlsList[i].url === '') continue;
        urls.push(this.urlsList[i].url);
      }
      if (urls.length < 1) return;
      if (!this.hasChanged) {
        this.SET_CONTENT_ITEMS_URLS_CHANGED(false);
        this.$emit('update-step');
      } else if (this.scrapingBatchId === 0) {
        this.SET_CONTENT_ITEMS_URLS_CHANGED(true);
        this.scrapeContent(urls);
      } else {
        this.SET_CONTENT_ITEMS_URLS_CHANGED(true);
        this.rescrapeContent(urls);
      }
    },
    scrapeContent(urls) {
      const scrapingPayload = {
        advertisersId: this.campaign.advertiserId,
        feedId: this.feedId,
        campaignId: this.campaign.id,
        urls: [...new Set(urls)],
      };
      this.SCRAPE_CONTENT(scrapingPayload)
        .then(() => {
          setTimeout(() => {
            this.$emit('update-step');
          }, 1000);
        });
    },
    async rescrapeContent(urls) {
      const rescrapingPayload = {
        campaignId: this.campaign.id,
        advertisersId: this.campaign.advertiserId,
        feedId: this.feedId,
        contentScrapeId: this.scrapingBatchId,
        urls: [...new Set(urls)],
      };
      await this.RESCRAPE_CONTENT(rescrapingPayload);
      if (this.GET_IS_VIEW_TYPE('Editor')) this.FETCH_ARTICLES();
      else this.$emit('update-step');
    },
    populatePageForScrapedContent() {
      if (helper.objLength(this.scrapedContent) === 0) return;
      for (const contentItem of this.scrapedContent) {
        this.urlsList.push(contentItem);
      }
    },
    emitNavigationValidity(validForNavigation) {
      this.$emit('setNotValidForNavigation', 'last', (!validForNavigation));
      this.$emit('setNotValidForNavigation', 'next', (!validForNavigation));
      this.$emit('setNotValidForNavigation', 'submit', (!validForNavigation));
    },
    checkAllValidUrls() {
      if (this.urlsList.length < 1) this.emitNavigationValidity(false);
      const validUrls = [];
      const allValidUrls = () => validUrls.every((validUrl) => validUrl === true);
      this.$nextTick(() => {
        for (const property in this.$refs.input) {
          if (this.$refs.input[property].state != null) {
            validUrls.push(this.$refs.input[property].state);
          }
        }
        if (validUrls.length < 1) validUrls.push(false);
        this.emitNavigationValidity(allValidUrls());
      });
    },
  },
  async created() {
    await this.NEW_FETCH_CONTENT_SCRAPES();
    this.populatePageForScrapedContent();
    this.checkAllValidUrls();
  },
};
</script>
<style scoped>
.input{
  padding-bottom: 25px;
}
.input-icon-addon{
  max-height: 38px;
}
.invalid-feedback{
  position: absolute;
  bottom: 0;
  left: 0;
}
.contentInfo {
    height: 100%;
    position: relative;
}

</style>
